import React from "react";
import styled, { keyframes } from "styled-components";
// Icons from react-icons
import { FaChartLine, FaLeaf } from "react-icons/fa6";
import { FaShieldAlt } from "react-icons/fa";
import { BsCheckCircleFill } from "react-icons/bs";

// Animation keyframes for bubbles
const float = keyframes`
  0%, 100% {
    transform: translateY(0px) translateX(0px);
    opacity: 0.7;
  }
  33% {
    transform: translateY(-20px) translateX(10px);
    opacity: 0.9;
  }
  66% {
    transform: translateY(10px) translateX(-10px);
    opacity: 0.6;
  }
`;

// Animated background component for Impact
const ImpactAnimatedBackground = () => (
  <ImpactBackgroundContainer>
    <ImpactFloatingOrb delay="0s" size="70px" />
    <ImpactFloatingOrb delay="1.5s" size="90px" />
    <ImpactFloatingOrb delay="3s" size="55px" />
    <ImpactFloatingOrb delay="4.5s" size="80px" />
    <ImpactFloatingOrb delay="6s" size="65px" />
    <ImpactFloatingOrb delay="7.5s" size="100px" />
  </ImpactBackgroundContainer>
);

export default function Impact() {
  const impactAreas = [
     {
      title: "Compliance & Risk",
      description: "Advanced KYC verification and fraud detection protecting financial institutions and enterprises from compliance risks.",
      metrics: [
        "Fraud detection rate: 98%+",
        "KYC processing time reduced by 80%",
        "Regulatory compliance maintained",
        "Risk assessment in real-time"
      ],
      icon: <FaShieldAlt />
    },
     {
      title: "Business Intelligence",
      description: "Real-time analytics and multilingual AI support driving data-driven decision-making and operational efficiency across industries.",
      metrics: [
        "Real-time data processing and insights",
        "Multilingual AI support for 10+ languages",
        "Operational efficiency improvements",
        "Data-driven decision-making enabled"
      ],
      icon: <FaChartLine />
    },
    {
      title: "Precision Agriculture",
      description: "Real-time disease detection and yield optimization for improved food security and farmer profitability across Africa.",
      metrics: [
        "Disease detection accuracy: 95%+",
        "Yield improvements: 20-30%",
        "Cost optimization in crop management",
        "Data-driven farming decisions"
      ],
      icon: <FaLeaf />
    },
    {
      title: "ESG & Sustainability",
      description: "Carbon emissions tracking and environmental compliance helping enterprises meet sustainability commitments.",
      metrics: [
        "Automated ESG reporting and monitoring",
        "Carbon footprint reduction tracking",
        "Regulatory compliance assurance",
        "Sustainability ROI measurement"
      ],
      icon: <FaLeaf />
    }
  ];

  return (
    <Wrapper id="impact">
      <div className="darkBg" style={{ padding: "100px 0", position: "relative", overflow: "hidden" }}>
        <ImpactAnimatedBackground />
        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <HeaderInfo>
            <h1 className="font40 extraBold whiteColor">Impact & Results</h1>
            <p className="font16 whiteColor">
              Anton Tech's five core pillars deliver measurable impact across agriculture, sustainability, compliance, insurance, and business intelligence, driving economic growth and innovation across Africa.
            </p>
          </HeaderInfo>

          <ImpactGrid>
            {impactAreas.map((area, index) => (
              <ImpactCard key={index}>
                <ImpactIcon>{area.icon}</ImpactIcon>
                <CardTitle className="font24 extraBold">{area.title}</CardTitle>
                <CardDescription className="font14">{area.description}</CardDescription>
                <MetricsList>
                  {area.metrics.map((metric, metricIndex) => (
                    <MetricItem key={metricIndex} className="font13">
                      <MetricIcon><BsCheckCircleFill /></MetricIcon>
                      {metric}
                    </MetricItem>
                  ))}
                </MetricsList>
              </ImpactCard>
            ))}
          </ImpactGrid>

          <VisionSection>
            <VisionTitle className="font32 extraBold whiteColor">
              Supporting Botswana Vision 2036
            </VisionTitle>
            <VisionGrid>
              <VisionItem>
                <VisionItemTitle className="font18 extraBold">Sustainable Economic Development</VisionItemTitle>
                <VisionItemText className="font14">
                  Driving economic diversification through AI-powered solutions, creating technology jobs, and enabling cross-industry innovation and growth.
                </VisionItemText>
              </VisionItem>
              <VisionItem>
                <VisionItemTitle className="font18 extraBold">Human & Social Development</VisionItemTitle>
                <VisionItemText className="font14">
                  Empowering youth and professionals with AI skills and digital literacy to prepare Botswana's workforce for the knowledge economy.
                </VisionItemText>
              </VisionItem>
              <VisionItem>
                <VisionItemTitle className="font18 extraBold">Environmental Sustainability</VisionItemTitle>
                <VisionItemText className="font14">
                  Through comprehensive ESG solutions, carbon mapping, and emissions analysis, safeguarding environmental resources for future generations.
                </VisionItemText>
              </VisionItem>
            </VisionGrid>
          </VisionSection>
        </div>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.section`
  width: 100%;
`;

const HeaderInfo = styled.div`
  text-align: center;
  margin-bottom: 80px;
  
  h1 {
    margin-bottom: 20px;
  }
  
  p {
    max-width: 700px;
    margin: 0 auto;
    opacity: 0.9;
  }
`;

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
  margin-bottom: 100px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(7, 148, 135, 0.2);
  }
`;

const ImpactIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
  color: #079487;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(7, 148, 135, 0.1);
  border-radius: 50%;
  margin: 0 auto 20px;
  transition: all 0.3s ease;
  
  svg {
    width: 48px;
    height: 48px;
  }
`;

const CardTitle = styled.h3`
  color: #079487;
  margin-bottom: 15px;
`;

const CardDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 25px;
`;

const MetricsList = styled.ul`
  list-style: none;
  padding: 0;
  text-align: left;
`;

const MetricItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  color: #333;
`;

const MetricIcon = styled.span`
  background: #079487;
  color: white;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
  flex-shrink: 0;
`;

const VisionSection = styled.div`
  text-align: center;
`;

const VisionTitle = styled.h2`
  margin-bottom: 50px;
  background: linear-gradient(135deg, #079487 0%, #079487 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const VisionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
`;

const VisionItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 30px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: left;
  
  @media (min-width: 769px) {
    backdrop-filter: blur(10px);
  }
  
  @media (max-width: 768px) {
    background: rgba(255, 255, 255, 0.15);
    padding: 20px;
  }
`;

const VisionItemTitle = styled.h4`
  color: #079487;
  margin-bottom: 15px;
`;

const VisionItemText = styled.p`
  color: white;
  line-height: 1.6;
  opacity: 0.9;
`;

// Impact section animated background components
const ImpactBackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const ImpactFloatingOrb = styled.div`
  position: absolute;
  width: ${props => props.size || '70px'};
  height: ${props => props.size || '70px'};
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(7, 148, 135, 0.15) 0%, rgba(102, 187, 106, 0.10) 100%);
  animation: ${float} 10s ease-in-out infinite;
  animation-delay: ${props => props.delay};
  
  &:nth-child(1) {
    top: 15%;
    right: 10%;
  }
  
  &:nth-child(2) {
    bottom: 25%;
    left: 8%;
  }
  
  &:nth-child(3) {
    top: 60%;
    right: 15%;
  }
  
  &:nth-child(4) {
    top: 40%;
    left: 20%;
  }
  
  &:nth-child(5) {
    bottom: 50%;
    right: 30%;
  }
  
  &:nth-child(6) {
    top: 20%;
    left: 40%;
  }
`;
